import { normalizeCreatorIdParam } from "./creator-id-param";
import { JOIN_RETURN_SRC } from "./creator-join-auth";

const LOCAL_ORIGIN = "https://local.shortformnation.invalid";


export const CREATOR_NOT_LINKED_LOGIN = "/login?accountIssue=creator_not_linked";

/**
 * Account-link recovery internal control routes. Both are *control* destinations,
 * not user landing pages: post-login routing sends an authenticated unlinked
 * creator to /account-link/complete, which consumes any same-browser proof and
 * only bounces to /account-link/recovery when no usable proof exists.
 */
export const ACCOUNT_LINK_RECOVERY_PATH = "/account-link/recovery";
export const ACCOUNT_LINK_COMPLETE_PATH = "/account-link/complete";

/**
 * Account-first Content Reward registration completion — a *control*
 * destination like /account-link/complete (2026-09-15, CR-AF-09). A session
 * loss at the completion route must resume DIRECTLY on it after login so the
 * account-bound proof stays unconsumed; wrapping it in /account-link/complete
 * would let THAT route consume and clear the proof, and the completion action
 * then fails with "TikTok verification is required".
 */
export const CONTENT_REWARD_REGISTRATION_COMPLETE_PATH =
  "/content-reward-registration/complete";

/**
 * Allowlisted entry sources for the account-link recovery funnel (Phase 7
 * observability). Anything else is normalized to `unknown`.
 */
export const ACCOUNT_LINK_ENTRY_SOURCES = [
  "plain_login",
  "creator_deep_link",
  "content_reward",
  "onboarding_direct",
  "checkout",
  "accept_terms",
  "unknown",
] as const;
export type AccountLinkEntrySource = (typeof ACCOUNT_LINK_ENTRY_SOURCES)[number];

/** Sanitized return-to destinations are bounded to 1,024 UTF-8 bytes before percent encoding. */
export const ACCOUNT_LINK_RETURN_TO_MAX_BYTES = 1024;

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/**
 * Sanitize a local destination AND bound it to the account-link return-to
 * limit. Returns null when absent, external, protocol-relative, too long, or
 * otherwise unsafe — callers sign `/creator_studio` in that case.
 */
export function sanitizeAccountLinkReturnTo(
  value: string | null | undefined
): string | null {
  const safe = sanitizeRedirectTo(value);
  if (!safe) return null;
  if (utf8ByteLength(safe) > ACCOUNT_LINK_RETURN_TO_MAX_BYTES) return null;
  return safe;
}

/**
 * Normalize a caller-supplied entry source to the allowlist. Used at the
 * account-link recovery surface and echoed into signed OAuth state.
 */
export function normalizeAccountLinkEntrySource(
  value: string | null | undefined
): AccountLinkEntrySource {
  return ACCOUNT_LINK_ENTRY_SOURCES.includes(value as AccountLinkEntrySource)
    ? (value as AccountLinkEntrySource)
    : "unknown";
}

/**
 * Derive the account-link entry source from a safe local destination when the
 * caller did not supply one. Content Reward, checkout, and accept-terms
 * continuations keep their own bucket; creator-shaped deep-links get
 * creator_deep_link; plain sign-in defaults to plain_login.
 */
export function deriveAccountLinkEntrySourceFromReturnTo(
  safeReturnTo: string | null | undefined
): AccountLinkEntrySource {
  if (!safeReturnTo) return "plain_login";
  if (safeReturnTo.includes("/content_rewards")) return "content_reward";
  if (safeReturnTo.includes("/checkout")) return "checkout";
  if (safeReturnTo.includes("/accept-terms")) return "accept_terms";
  if (safeReturnTo.includes("/onboarding")) return "onboarding_direct";
  if (
    safeReturnTo.startsWith("/creator_studio") ||
    safeReturnTo.startsWith("/profile") ||
    safeReturnTo.startsWith("/insights")
  ) {
    return "creator_deep_link";
  }
  return "plain_login";
}

/** Build the completion-route URL that preserves a safe destination through linking. */
export function buildAccountLinkCompleteUrl(safeDestination: string): string {
  return `${ACCOUNT_LINK_COMPLETE_PATH}?returnTo=${encodeURIComponent(safeDestination)}`;
}

/**
 * Build the explicit recovery destination for a Content Reward join attempt.
 * The attempt id is correlation only; creator ownership is still established
 * exclusively by the authenticated session plus a TikTok-verified link proof.
 */
export function buildContentRewardAccountLinkRecoveryUrl(
  rewardId: string,
  joinAttemptId: string | null
): string {
  const returnParams = new URLSearchParams({ src: "discord" });
  if (joinAttemptId) returnParams.set("join_attempt_id", joinAttemptId);
  const returnTo = `/creator_studio/content_rewards/browse/${encodeURIComponent(
    rewardId
  )}?${returnParams.toString()}`;
  const recoveryParams = new URLSearchParams({
    reason: "oauth_failed",
    entry_source: "content_reward",
    returnTo,
  });
  return `${ACCOUNT_LINK_RECOVERY_PATH}?${recoveryParams.toString()}`;
}

export function buildContentRewardRecoveryLoginUrl(
  rewardId: string,
  joinAttemptId: string | null
): string {
  return `/login?redirectTo=${encodeURIComponent(
    buildContentRewardAccountLinkRecoveryUrl(rewardId, joinAttemptId)
  )}`;
}

/**
 * Only permit same-origin relative destinations so login cannot become an open redirect.
 *
 * The guard validates the *output* after WHATWG URL normalization, not just the
 * raw input: resolving a dot-segment path like "/..//evil.example" against the
 * local origin collapses it into the pathname "//evil.example" — the exact
 * protocol-relative shape that, emitted verbatim into `window.location.href` or
 * a Location header, navigates the browser to an external host (AQ-10).
 */
export function sanitizeRedirectTo(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;

  try {
    const parsed = new URL(value, LOCAL_ORIGIN);
    if (parsed.origin !== LOCAL_ORIGIN) return null;
    const destination = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    // Reject any result that is not a path beginning with a single "/" (i.e. no
    // leading "//"). Every escape vector funnels through here, so this output
    // check is the invariant, not the input pre-checks above.
    if (!/^\/(?!\/)/.test(destination)) return null;
    return destination;
  } catch {
    return null;
  }
}

/** Build a login URL that preserves the route and query string being guarded. */
export function buildLoginRedirect(request: Request): string {
  const url = new URL(request.url);
  const returnTo = `${url.pathname}${url.search}`;
  return `/login?redirectTo=${encodeURIComponent(returnTo)}`;
}

/** Extract a creator identity only from a creator-facing local return path. */
export function getCreatorIdFromRedirectTo(value: string | null | undefined): string | null {
  const safeValue = sanitizeRedirectTo(value);
  if (!safeValue) return null;

  const url = new URL(safeValue, LOCAL_ORIGIN);
  if (
    url.pathname !== "/profile" &&
    url.pathname !== "/insights" &&
    url.pathname !== "/creator_studio" &&
    !url.pathname.startsWith("/creator_studio/")
  ) {
    return null;
  }

  const creatorId = url.searchParams.get("creator_id")?.trim();
  if (!creatorId || !/^[A-Za-z0-9._-]{1,64}$/.test(creatorId)) return null;
  return creatorId;
}

/**
 * The creator a /join or /insights return may claim with the unsigned handle in the link
 * (Tad, 2026-11-02, widened 2026-11-03: /join is open to the world, and a creator's own
 * link is enough to claim their handle).
 *
 * Only onboarding returns qualify. A legacy `/creator_studio?creator_id=` return keeps its
 * TikTok-proof account-link leg, and /join's own Creator Studio destination marks itself
 * with `src=join` so the two cannot be confused.
 *
 * The handle is NOT held to the strict regex above: live creator IDs carry emoji and curly
 * quotes (`YaYiTa❤️🌎`, `jemma’s`), and this value only ever reaches the link policy, which
 * does its own checks.
 */
export function getHandleClaimCreatorId(redirectTo: string | null | undefined): string | null {
  const safe = sanitizeRedirectTo(redirectTo);
  if (!safe) return null;
  const url = new URL(safe, LOCAL_ORIGIN);
  const onboardingReturn =
    url.pathname === "/insights" ||
    url.pathname === "/join" ||
    (url.pathname === "/creator_studio" && url.searchParams.get("src") === JOIN_RETURN_SRC);
  if (!onboardingReturn) return null;
  return normalizeCreatorIdParam(url.searchParams.get("creator_id"));
}

export const CREATOR_NOT_LINKED_MESSAGE =
  "This account is not linked to a creator profile. Reopen your creator profile link and sign in again.";interface CreatorNotLinkedBlockArgs {
  /** Value of the `accountIssue` query param on the login page. */
  accountIssue: string | null;
  /** Where the user was headed before being bounced to login, if anywhere. */
  redirectTo: string | null;
  isBrandManager: boolean;
  /** Whether the signed-in account actually has a creator_id in BetterAuth's "user" table. */
  hasLinkedCreator: boolean;
}

/**
 * After a successful sign-in from `/login?accountIssue=creator_not_linked`,
 * decide whether the account genuinely has no creator profile to land on.
 *
 * ⚠️ This must be driven by the real link state, not by the presence of the
 * `accountIssue` param. The param only records that *some* route claimed the
 * account was unlinked; if that route was wrong (it read the legacy `users`
 * table instead of BetterAuth's `"user"` — see getLinkedCreatorId), blocking on
 * the param alone traps a correctly-linked creator in a login loop they cannot
 * escape, because the redirect carries no `redirectTo` to sanitize.
 */
export function shouldBlockCreatorNotLinked({
  accountIssue,
  redirectTo,
  isBrandManager,
  hasLinkedCreator,
}: CreatorNotLinkedBlockArgs): boolean {
  if (accountIssue !== "creator_not_linked") return false;
  if (isBrandManager) return false;
  // A linked creator has somewhere to go (/profile self-heals its creator_id).
  if (hasLinkedCreator) return false;
  // An unlinked account can still be rescued by a return path carrying a
  // creator_id — resolvePostLoginDestination will link it on the way through.
  if (getCreatorIdFromRedirectTo(redirectTo)) return false;
  return true;
}

interface ResolvePostLoginDestinationArgs {
  redirectTo: string | null | undefined;
  isBrandManager: boolean;
  hasLinkedCreator: boolean;
  /**
   * A linked creator's home when nothing names a destination: `/insights` once they
   * have had a creator agent run (`creatorHomePath`, planning/11_02 W14). Anything
   * else reads as `/creator_studio`.
   */
  creatorHome?: string | null;
}

/**
 * Resolve the post-login destination deterministically (Phase 2 of the
 * cross-browser account-linking RCA):
 *   1. brand manager → /dashboard;
 *   2. linked creator → the safe requested destination, else their home
 *      (`/insights` for a creator agent creator, else /creator_studio);
 *   3. unlinked creator → /account-link/complete?returnTo=<safe-destination>,
 *      which consumes any same-browser proof and only shows recovery when no
 *      usable proof exists.
 *
 * A genuinely unlinked non-brand account is NEVER sent to /profile as a
 * terminal destination.
 */
export function resolvePostLoginDestination({
  redirectTo,
  isBrandManager,
  hasLinkedCreator,
  creatorHome,
}: ResolvePostLoginDestinationArgs): string {
  if (isBrandManager) return "/dashboard";

  const safeRedirect = sanitizeRedirectTo(redirectTo);

  // /account-link/recovery, /account-link/complete and the account-first
  // content-reward registration completion are internal CONTROL destinations,
  // not user landing pages. When one is already the sanitized redirectTo (e.g.
  // requireAuth restored a lost session on the completion route), navigate to
  // it directly — never wrap it in another completion URL. Wrapping the
  // registration completion would let account-link/complete consume and clear
  // the account-bound proof, and the completion action then 403s.
  if (
    safeRedirect &&
    (safeRedirect === ACCOUNT_LINK_COMPLETE_PATH ||
      safeRedirect === ACCOUNT_LINK_RECOVERY_PATH ||
      safeRedirect.startsWith(`${ACCOUNT_LINK_COMPLETE_PATH}?`) ||
      safeRedirect.startsWith(`${ACCOUNT_LINK_RECOVERY_PATH}?`) ||
      safeRedirect === CONTENT_REWARD_REGISTRATION_COMPLETE_PATH ||
      safeRedirect.startsWith(`${CONTENT_REWARD_REGISTRATION_COMPLETE_PATH}?`))
  ) {
    return safeRedirect;
  }

  if (hasLinkedCreator) return safeRedirect ?? (creatorHome === "/insights" ? "/insights" : "/creator_studio");

  return buildAccountLinkCompleteUrl(safeRedirect ?? "/creator_studio");
}
